import { DateTime, Duration, Settings } from "luxon";

// Set default locale to Indonesian
Settings.defaultLocale = 'id';

export const DEFAULT_DATE_FORMAT = "dd MMM yyyy • HH:mm";
export const DATE_TIME_FORMAT = "dd MMM yyyy • HH:mm:ss";

export function getDateStr(date: any) {
  return DateTime.fromISO(date).setLocale('id').toFormat("dd MMM yyyy");
}

export function getDate(date: any) {
  return getFormattedDate(date, "dd MMM yyyy");
}

export function getDay(date: any) {
  return DateTime.fromISO(date).setLocale('id').toFormat("cccc"); // Nama hari lengkap
}

export function getDateDuration(date: any) {
  if (!date) return "-";

  const d = DateTime.fromISO(date);
  const now = DateTime.now();
  const seconds = Math.floor(now.diff(d, "seconds").seconds);

  if (seconds < 60) {
    return seconds === 1 ? "1 detik yang lalu" : `${seconds} detik yang lalu`;
  }

  const minutes = Math.round(seconds / 60);
  if (minutes < 60) {
    return minutes === 1 ? "1 menit yang lalu" : `${minutes} menit yang lalu`;
  }

  const hours = Math.round(minutes / 60);
  if (hours < 24) {
    return hours === 1 ? "1 jam yang lalu" : `${hours} jam yang lalu`;
  }

  const days = Math.round(hours / 24);
  if (days < 14) {
    return days === 1 ? "1 hari yang lalu" : `${days} hari yang lalu`;
  }

  const weeks = Math.round(days / 7);
  if (weeks < 4) {
    return weeks === 1 ? "1 minggu yang lalu" : `${weeks} minggu yang lalu`;
  }

  const months = Math.round(weeks / 4);
  if (months < 4) {
    return months === 1 ? "1 bulan yang lalu" : `${months} bulan yang lalu`;
  }

  return d.setLocale('id').toFormat(DEFAULT_DATE_FORMAT);
}

export function getFormattedDate(
  date: any,
  format?: string,
  isNoYesterday = false
) {
  if (!date) return "-";

  const d = DateTime.fromISO(date).setLocale('id');
  const now = DateTime.now().setLocale('id');
  const fmt = format || DEFAULT_DATE_FORMAT;
  const dateStrYMD = parseInt(d.toFormat("yyyyMMdd"));
  const nowYMD = parseInt(now.toFormat("yyyyMMdd"));
  const isPassedOneDay = nowYMD - dateStrYMD === 1;

  if (now.hasSame(d, "day") && !format) {
    return `Hari ini, ${d.toFormat("HH:mm")}`;
  } else if (fmt !== "HH:mm" && isPassedOneDay && !isNoYesterday && !format) {
    return `Kemarin, ${d.toFormat("HH:mm")}`;
  } else {
    return d.toFormat(fmt);
  }
}

export function getTime(date: any) {
  return getFormattedDate(date, "HH:mm");
}

export function getTime12(date: any) {
  const time = getFormattedDate(date, "hh:mm a");
  return time !== "Yesterday" && time;
}

export const createDeadline = (
  date: string,
  additionalHour = 0,
  unit: keyof Duration = "hours"
) => {
  return DateTime.fromISO(date).plus({ [unit]: additionalHour });
};

export const createDeadlineMinutes = (date: string, additionalMinute = 0) => {
  return DateTime.fromISO(date).plus({ minutes: additionalMinute });
};

export function getDeadline(duration: number, unit: keyof Duration) {
  return DateTime.now()
    .plus({ [unit]: duration })
    .toFormat(DATE_TIME_FORMAT);
}

export function generateDeadline(
  startTime: any,
  amount: number,
  unit: keyof Duration
) {
  return DateTime.fromISO(startTime)
    .plus({ [unit]: amount })
    .toFormat(DATE_TIME_FORMAT);
}

export function isPassDeadline(deadline: string | number) {
  const dl =
    typeof deadline === "number"
      ? DateTime.fromMillis(deadline)
      : DateTime.fromISO(deadline.toString());
  return dl < DateTime.now();
}

export function isPassDeadlineUnix(deadline: number) {
  return Date.now() > deadline;
}

export function calculateNumberOfDays(dueDate: any) {
  const now = DateTime.now();
  const endDate = DateTime.fromISO(dueDate);
  const diff = Math.ceil(endDate.diff(now, "days").days);
  return `${diff} days`;
}

export function calculateNumberOfMinutes(dueDate: any) {
  const now = DateTime.now();
  const endDate = DateTime.fromISO(dueDate);
  const diff = Math.floor(endDate.diff(now, "minutes").minutes * -1);
  return diff;
}

export function countDate(from: any, to: any) {
  const fromDate = DateTime.fromISO(from);
  const toDate = DateTime.fromISO(to);
  const diff = Math.ceil(toDate.diff(fromDate, "days").days);
  return `${diff} days`;
}

export function getRemainingTime(deadline: string): number {
  const dl = DateTime.fromISO(deadline);
  return dl.diffNow().milliseconds;
}

export const calculateRemainingTime = (endDate: string) => {
  const end = new Date(endDate);
  const now = new Date();
  const diffTime = end.getTime() - now.getTime();
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

  if (diffDays < 0) return "Sudah berakhir";

  const years = Math.floor(diffDays / 365);
  const months = Math.floor((diffDays % 365) / 30);

  if (years > 0 && months > 0) {
    return `${years} tahun ${months} bulan lagi`;
  } else if (years > 0) {
    return `${years} tahun lagi`;
  } else if (months > 0) {
    return `${months} bulan lagi`;
  } else {
    return `${diffDays} hari lagi`;
  }
};

export const calculateTimePeriod = (startDate: string, endDate: string) => {
  const end = new Date(endDate);
  const now = new Date(startDate);
  const diffTime = end.getTime() - now.getTime();
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

  if (diffDays < 0) return "Sudah berakhir";

  const years = Math.floor(diffDays / 365);
  const months = Math.floor((diffDays % 365) / 30);

  if (years > 0 && months > 0) {
    return `${years} tahun ${months} bulan`;
  } else if (years > 0) {
    return `${years} tahun`;
  } else if (months > 0) {
    return `${months} bulan`;
  } else {
    return `${diffDays} hari`;
  }
};

export const calculateTimeLeft = (endDate: string): any => {
  const dueDate = new Date(endDate).getTime();
  const now = Date.now();
  const difference = dueDate - now;

  if (difference <= 0 || !endDate) {
    return {
      days: 0,
      hours: 0,
      minutes: 0,
      seconds: 0,
      isExpired: true,
    };
  }

  const days = Math.floor(difference / (1000 * 60 * 60 * 24));
  const hours = Math.floor(
    (difference % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)
  );
  const minutes = Math.floor((difference % (1000 * 60 * 60)) / (1000 * 60));
  const seconds = Math.floor((difference % (1000 * 60)) / 1000);

  return {
    days,
    hours,
    minutes,
    seconds,
    isExpired: false,
  };
};
